import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Fab,
  IconButton,
  Paper,
  Slide,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import RemoveIcon from '@mui/icons-material/Remove';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SendIcon from '@mui/icons-material/Send';
import { alpha } from '@mui/material/styles';
import { assetsApi } from '../../services/api';
import { SEARCE_COLORS } from '../../theme';
import type { AssetAiChatHistoryMessage, AssetAiQueryResponse } from '../../types/assets';

export interface AssetAiChatDialogProps {
  /** If provided, shows a control to open the asset detail when AI returns an asset_id */
  onOpenAssetByCode?: (assetCode: string) => void;
  /**
   * When the API returns `sources`, `assets`, or `asset_id`, parent filters the table to
   * every unique asset ID in first-seen order (same as Clear All Filters).
   */
  onAiSuggestedAssetIds?: (assetIdsInOrder: string[]) => void;
}

/** Shown in the chat bubble after the top match (not applied to table filter). */
const CHAT_ADDITIONAL_ASSET_ROWS = 2;
/** Max transcript entries sent as `chat_history` (excluding the current `user_question`). */
const CHAT_HISTORY_MESSAGE_LIMIT = 8;
/** Cap assistant text in `chat_history` so citations/snippets are not replayed in full. */
const ASSISTANT_HISTORY_ANSWER_MAX_CHARS = 2000;

function truncateForHistory(text: string, maxLen: number): string {
  const t = text.trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen)}…`;
}

/**
 * Compact assistant line for the next request: answer (truncated) + top source asset_id/name only.
 */
function buildAssistantHistoryContent(res: AssetAiQueryResponse): string {
  const answer = truncateForHistory(res.answer || '', ASSISTANT_HISTORY_ANSWER_MAX_CHARS);
  const top = res.sources?.[0];
  if (top?.asset_id) {
    const name = top.asset_name?.trim() || '';
    return name
      ? `${answer}\n\nasset_id: ${top.asset_id}\nasset_name: ${name}`
      : `${answer}\n\nasset_id: ${top.asset_id}`;
  }
  const id = res.asset_id?.trim();
  if (id) {
    return `${answer}\n\nasset_id: ${id}`;
  }
  return answer;
}

/** Unique asset IDs in order of first appearance (full list for table filter). */
function assetIdsFromAiResponse(res: AssetAiQueryResponse): string[] {
  const pushUnique = (out: string[], seen: Set<string>, raw: string | undefined | null) => {
    const id = raw?.trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  };

  const seen = new Set<string>();
  const out: string[] = [];

  if (res.sources?.length) {
    for (const s of res.sources) {
      pushUnique(out, seen, s.asset_id);
    }
  }

  if (res.assets?.length) {
    for (const id of res.assets) {
      pushUnique(out, seen, id);
    }
  }

  if (res.asset_id) {
    pushUnique(out, seen, res.asset_id);
  }

  return out;
}

type ChatRole = 'user' | 'assistant';

interface ChatMessage {
  id: string;
  role: ChatRole;
  text: string;
  /** Original filename when a file was sent with this user message */
  attachmentName?: string;
  assetId?: string | null;
  /** Next matches after `assetId` (chat only; table uses full list from the same response). */
  additionalAssetIds?: string[];
  isError?: boolean;
  /** What we send as assistant `content` in `chat_history` (answer + top source ids only). */
  apiHistoryContent?: string;
}

function priorMessagesToChatHistory(messages: ChatMessage[]): AssetAiChatHistoryMessage[] {
  const relevant = messages.filter(m => !m.isError).slice(-CHAT_HISTORY_MESSAGE_LIMIT);
  return relevant.map(m => ({
    role: m.role,
    content:
      m.role === 'assistant'
        ? m.apiHistoryContent ?? truncateForHistory(m.text, ASSISTANT_HISTORY_ANSWER_MAX_CHARS)
        : m.text.trim(),
  }));
}

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const STORAGE_KEY = 'deliverypulse_asset_ai_chat_v1';

type PersistedChatPayload = {
  v: 3;
  messages: ChatMessage[];
  expanded: boolean;
};

function loadPersisted(): { messages: ChatMessage[]; expanded: boolean } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as {
      v?: number;
      messages?: unknown;
      expanded?: boolean;
    };
    if (!Array.isArray(data.messages)) return null;
    if (data.v !== undefined && data.v !== 1 && data.v !== 2 && data.v !== 3) return null;
    const messages = data.messages.filter((m: unknown): m is ChatMessage => {
      if (!m || typeof m !== 'object') return false;
      const o = m as Record<string, unknown>;
      return (
        typeof o.id === 'string' &&
        (o.role === 'user' || o.role === 'assistant') &&
        typeof o.text === 'string'
      );
    });
    return {
      messages: messages.map(m => ({
        ...m,
        attachmentName: typeof m.attachmentName === 'string' ? m.attachmentName : undefined,
        assetId: m.assetId === null || typeof m.assetId === 'string' ? m.assetId : undefined,
        additionalAssetIds: Array.isArray(m.additionalAssetIds)
          ? m.additionalAssetIds.filter((x): x is string => typeof x === 'string')
          : undefined,
        isError: m.isError === true ? true : undefined,
        apiHistoryContent:
          typeof m.apiHistoryContent === 'string' ? m.apiHistoryContent : undefined,
      })),
      expanded: typeof data.expanded === 'boolean' ? data.expanded : true,
    };
  } catch {
    return null;
  }
}

function savePersisted(messages: ChatMessage[], expanded: boolean) {
  try {
    const payload: PersistedChatPayload = { v: 3, messages, expanded };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota / private mode */
  }
}

/**
 * Bottom-right chat: large centered window vs compact floating window (minimize keeps chat visible).
 * Transcript + layout preference in localStorage. Large size matches squad detail dialog (`maxWidth` lg, ~80vh).
 */
const AssetAiChatDialog: React.FC<AssetAiChatDialogProps> = ({
  onOpenAssetByCode,
  onAiSuggestedAssetIds,
}) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [input, setInput] = useState('');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const saved = loadPersisted();
    if (saved) {
      setMessages(saved.messages);
      setExpanded(saved.expanded);
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    savePersisted(messages, expanded);
  }, [messages, expanded, hydrated]);

  useEffect(() => {
    if (!panelOpen) return;
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending, panelOpen]);

  const handleClosePanel = useCallback(() => {
    if (sending) return;
    setPanelOpen(false);
  }, [sending]);

  const collapseToStrip = useCallback(() => {
    if (sending) return;
    setExpanded(false);
  }, [sending]);

  const expandToWindow = useCallback(() => {
    if (sending) return;
    setExpanded(true);
  }, [sending]);

  const handleResetConversation = useCallback(() => {
    if (sending) return;
    setMessages([]);
    setInput('');
    setAttachedFile(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, [sending]);

  const handleSend = useCallback(async () => {
    const q = input.trim();
    const file = attachedFile;
    if ((!q && !file) || sending) return;

    const user_question = q || 'Please use the attached file for context.';
    const chat_history = priorMessagesToChatHistory(messages);
    const attachmentName = file?.name;

    setInput('');
    setMessages(prev => [
      ...prev,
      {
        id: newId(),
        role: 'user',
        text: q || 'Attachment',
        ...(attachmentName ? { attachmentName } : {}),
      },
    ]);
    setSending(true);

    try {
      const aiResponse = await assetsApi.aiQuery({
        user_question,
        ...(chat_history.length ? { chat_history } : {}),
        ...(file ? { file } : {}),
      });
      setAttachedFile(null);
      const tableIds = assetIdsFromAiResponse(aiResponse);
      if (tableIds.length > 0) {
        onAiSuggestedAssetIds?.(tableIds);
      }
      const primaryAssetId =
        tableIds[0] ?? aiResponse.asset_id?.trim() ?? undefined;
      const additionalAssetIds = tableIds.slice(1, 1 + CHAT_ADDITIONAL_ASSET_ROWS);
      const assistantContextForApi = buildAssistantHistoryContent(aiResponse);
      setMessages(prev => [
        ...prev,
        {
          id: newId(),
          role: 'assistant',
          text: aiResponse.answer,
          assetId: primaryAssetId,
          additionalAssetIds:
            additionalAssetIds.length > 0 ? additionalAssetIds : undefined,
          apiHistoryContent: assistantContextForApi,
        },
      ]);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (e instanceof Error ? e.message : 'Something went wrong. Try again.');
      setMessages(prev => [
        ...prev,
        { id: newId(), role: 'assistant', text: String(msg), isError: true },
      ]);
    } finally {
      setSending(false);
    }
  }, [input, sending, messages, onAiSuggestedAssetIds]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const openAssetAndClose = (code: string) => {
    onOpenAssetByCode?.(code);
    setPanelOpen(false);
  };

  const expandedModal = expanded && panelOpen;

  return (
    <Box
      sx={{
        position: 'fixed',
        zIndex: theme =>
          expandedModal ? theme.zIndex.modal : theme.zIndex.drawer + 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: expandedModal ? 'stretch' : 'flex-end',
        gap: 1,
        ...(expandedModal
          ? {
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              bottom: 'auto',
              right: 'auto',
              width: theme =>
                `min(${theme.breakpoints.values.lg}px, calc(100vw - 48px))`,
              maxWidth: theme =>
                `min(${theme.breakpoints.values.lg}px, calc(100vw - 48px))`,
            }
          : {
              bottom: expanded ? 24 : 16,
              right: expanded ? 24 : 16,
              width: expanded
                ? 'min(560px, calc(100vw - 40px))'
                : 'min(400px, calc(100vw - 32px))',
              maxWidth: expanded
                ? 'min(560px, calc(100vw - 40px))'
                : 'min(400px, calc(100vw - 32px))',
            }),
        transition: theme =>
          theme.transitions.create(
            ['width', 'max-width', 'bottom', 'right', 'left', 'top', 'transform'],
            { duration: 220 }
          ),
      }}
    >
      <Slide direction="up" in={panelOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={expandedModal ? 12 : 6}
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: expanded ? 3 : 2,
            border: '1px solid',
            borderColor: 'divider',
            background: expandedModal ? 'white' : undefined,
            boxShadow: expandedModal ? '0 24px 48px rgba(0,0,0,0.15)' : undefined,
            minHeight: expandedModal ? '80vh' : expanded ? undefined : 'min(320px, 52vh)',
            maxHeight: expandedModal
              ? 'min(90vh, calc(100vh - 48px))'
              : expanded
                ? undefined
                : 'min(520px, 58vh)',
            transition: theme =>
              theme.transitions.create(
                ['max-height', 'min-height', 'box-shadow', 'border-radius'],
                { duration: 220 }
              ),
          }}
        >
          <Box
            sx={{
              px: 1.25,
              py: expanded ? 1.25 : 1,
              minHeight: expanded ? undefined : 48,
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              flexShrink: 0,
              bgcolor: expanded ? SEARCE_COLORS.primary : alpha(SEARCE_COLORS.neutral_500, 0.12),
              color: expanded ? SEARCE_COLORS.on_primary : 'text.primary',
              borderBottom: expanded ? 'none' : '1px solid',
              borderColor: 'divider',
              cursor: expanded ? 'default' : 'pointer',
            }}
            onClick={!expanded ? expandToWindow : undefined}
            role={!expanded ? 'button' : undefined}
            aria-expanded={expanded}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: expanded ? '1rem' : '0.875rem',
              }}
            >
              Ask about assets
              {!expanded && messages.length > 0
                ? ` · ${messages.length} message${messages.length === 1 ? '' : 's'}`
                : ''}
            </Typography>
            {expanded ? (
              <Tooltip title="Minimize to small window">
                <span>
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      collapseToStrip();
                    }}
                    disabled={sending}
                    sx={{ color: 'inherit' }}
                    aria-label="Minimize chat to small window"
                  >
                    <RemoveIcon sx={{ fontSize: '1.15rem' }} />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="Expand to large window">
                <span>
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      expandToWindow();
                    }}
                    disabled={sending}
                    sx={{ color: 'inherit' }}
                    aria-label="Expand chat"
                  >
                    <OpenInFullIcon sx={{ fontSize: '1.05rem' }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Close">
              <span>
                <IconButton
                  size="small"
                  edge="end"
                  onClick={e => {
                    e.stopPropagation();
                    handleClosePanel();
                  }}
                  disabled={sending}
                  sx={{ color: 'inherit' }}
                  aria-label="Close"
                >
                  <CloseIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <>
            <Box
              ref={scrollRef}
              sx={{
                flex: 1,
                minHeight: expandedModal ? 0 : expanded ? 200 : 160,
                maxHeight: expandedModal ? undefined : expanded ? undefined : 280,
                overflow: 'auto',
                p: expanded ? 2 : 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: expanded ? 1.5 : 1.25,
              }}
            >
                {messages.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Ask anything about Searce assets — migrations, accelerators, IP, or problems you are trying to solve.
                    Matching asset IDs can be opened in the table below.
                  </Typography>
                )}
                {messages.map(m => (
                  <Box
                    key={m.id}
                    sx={{
                      alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                      maxWidth: '92%',
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        bgcolor:
                          m.role === 'user'
                            ? SEARCE_COLORS.primary_lightest
                            : alpha(SEARCE_COLORS.primary, 0.06),
                        border: 1,
                        borderColor: m.isError ? 'error.light' : 'divider',
                        color: m.role === 'user' ? SEARCE_COLORS.textPrimary : 'text.primary',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.8125rem',
                          lineHeight: 1.45,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {m.text}
                      </Typography>
                      {m.role === 'user' && m.attachmentName && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Attachment: {m.attachmentName}
                        </Typography>
                      )}
                      {m.role === 'assistant' && m.assetId && !m.isError && (
                        <>
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                            <Chip size="small" label={m.assetId} variant="outlined" sx={{ fontWeight: 600 }} />
                            {onOpenAssetByCode && (
                              <Button
                                size="small"
                                variant="text"
                                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                onClick={() => openAssetAndClose(m.assetId!)}
                              >
                                Open asset
                              </Button>
                            )}
                          </Box>
                          {(m.additionalAssetIds?.length ?? 0) > 0 && (
                            <Box sx={{ mt: 1.5 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                                Here are other assets that might also be useful:
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {m.additionalAssetIds!.map(code => (
                                  <Box
                                    key={code}
                                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}
                                  >
                                    <Chip size="small" label={code} variant="outlined" sx={{ fontWeight: 600 }} />
                                    {onOpenAssetByCode && (
                                      <Button
                                        size="small"
                                        variant="text"
                                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                        onClick={() => openAssetAndClose(code)}
                                      >
                                        Open asset
                                      </Button>
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                ))}
                {sending && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, alignSelf: 'flex-start' }}>
                    <CircularProgress size={20} />
                    <Typography variant="caption" color="text.secondary">
                      Thinking…
                    </Typography>
                  </Box>
                )}
              </Box>

            <Box
              sx={{
                px: expanded ? 1.5 : 1.25,
                pb: 0.5,
                display: 'flex',
                gap: 1,
                alignItems: 'flex-end',
                flexShrink: 0,
              }}
            >
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) setAttachedFile(f);
                    e.target.value = '';
                  }}
                />
                <Tooltip title="Attach file">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={sending}
                      aria-label="Attach file"
                      color={attachedFile ? 'primary' : 'default'}
                    >
                      <AttachFileIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  size="small"
                  placeholder="e.g. I want to do migrations from AWS to GCP"
                  value={input}
                  disabled={sending}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
                />
                <IconButton
                  color="primary"
                  onClick={() => void handleSend()}
                  disabled={sending || (!input.trim() && !attachedFile)}
                  aria-label="Send"
                >
                  <SendIcon />
                </IconButton>
              </Box>

            {attachedFile && (
              <Box
                sx={{
                  px: expanded ? 1.5 : 1.25,
                  pb: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                  <Chip
                    size="small"
                    label={attachedFile.name}
                    onDelete={() => setAttachedFile(null)}
                    disabled={sending}
                  />
                </Box>
              )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pb: expanded ? 1 : 0.75,
                pt: 0.25,
                borderTop: '1px solid',
                borderColor: 'divider',
                flexShrink: 0,
              }}
            >
              <Button
                size="small"
                startIcon={<RestartAltIcon sx={{ fontSize: '1rem !important' }} />}
                onClick={handleResetConversation}
                disabled={sending || messages.length === 0}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  '&:hover': { bgcolor: alpha(SEARCE_COLORS.primary, 0.06) },
                }}
              >
                Reset conversation
              </Button>
            </Box>
          </>
        </Paper>
      </Slide>

      {!panelOpen && (
        <Fab
          color="primary"
          variant="extended"
          onClick={() => setPanelOpen(true)}
          sx={{
            boxShadow: 4,
            fontWeight: 700,
            letterSpacing: 0.02,
            textTransform: 'uppercase',
            fontSize: '0.7rem',
            px: 2,
          }}
        >
          <AutoAwesomeIcon sx={{ mr: 1 }} />
          Ask about assets
        </Fab>
      )}
    </Box>
  );
};

export default AssetAiChatDialog;
